import Marionette from "backbone.marionette";
import Radio from "backbone.radio";
import { generalizeRouteName, errorParser } from "../utils";

import ColumnModel from "../models/columns";
import tooltipConfigurator from "../utils/datatable/tooltip-configurator";
import ReportHistoryModel from "../models/report-history";
import routeButtons, {
  genericButtons,
} from "../config/datatable/action-buttons";
import environment from "./environment";

const globalChannel = Radio.channel("global");
const reportChannel = Radio.channel("report");

class DatatableConfigurator extends Marionette.Object {
  initialize(options) {
    // localize options
    this.eventName = options.eventName;
    this.endpoint = options.endpoint;
    this.route = options.route;
    this.routeConfig = options.routeConfig;
    this.dt_config = {};
    this.navigation = options.navigation;
    this.columnModel = options.columnModel;
    this.customColumns = options.customColumns;
    this.customColumnsCreated = options.customColumnsCreated;
    this.linkClassName = options.linkClassName || null;
    this.routeMatcher = generalizeRouteName(this.route);

    this.setupButtons();
    this.setupColumnModel();
  }

  /**
   * Determines whether a DT will allow for single or multiple row selection.
   *
   * See https://datatables.net/extensions/select/
   *
   * @param {Array} buttons
   */
  getRowSelectionStyle(buttons = []) {
    // list of classes extended by the buttons, without false values.
    const extensions = buttons
      .map((button) => button.extend)
      .filter((extension) => extension);

    // assume only single row selection will be possible
    let style = "single";
    // unless the "selected" or "collection" extension is found
    if (
      _.contains(extensions, "selected") ||
      _.contains(extensions, "collection")
    ) {
      // in which case, multiple selections are possible
      style = "os";
    }

    return style;
  }

  setupButtons() {
    // set default DT config
    Object.assign(this.dt_config, {
      select: false,
      buttons: genericButtons,
      // pass the original route name in case it needs to be reloaded
      route: this.route,
    });

    // get configuration for this route
    const config = routeButtons[this.routeMatcher] || {};
    // determine if chart review actions buttons need to be added
    if (config) {
      // validate roles
      if (reportChannel.request("user:has-any-role", config.roles)) {
        // determine row selection style
        const style = this.getRowSelectionStyle(config.buttons);

        // merge route specific and generic buttons
        const buttons = config.buttons.concat(genericButtons.buttons);

        // update DT config with new button set
        Object.assign(this.dt_config, {
          select: {
            style,
            blurable: false,
          },
          buttons,
        });
      }
    }
  }

  setupColumnModel() {
    // was an endpoint provided?
    if (!this.endpoint) {
      // if not, set ajax endpoint
      this.endpoint = this.getEndpoint();
    }

    // add ajax endpoint to generic model
    const Model = ColumnModel.extend({
      urlRoot: `${this.endpoint}?${this.getUrlParams()}`,
      initialize() {
        this.promise = this.fetch();
      },
    });

    // instatiate model
    this.columnModel = new Model();
    // handle promise
    this.columnModel.promise.done(this._onLoad.bind(this));
    this.columnModel.promise.fail(this._onLoadFailure.bind(this));
  }

  getEndpoint() {
    // get route type
    let routeType = this.route.split(".")[0] || null;

    // use entire route name for certain routes
    if (_.contains(["admin", "tools", "chart-reviews"], routeType)) {
      routeType = this.route;
    }

    // set endpoint for given route type
    let endpoint = environment.getEndpoint(routeType, this.routeConfig);
    // if this item has a custom URL use it instead of original endpoint
    if (this.navigation.url) {
      endpoint = environment.getApiHost() + this.navigation.url;
    }

    if (!endpoint) {
      console.warn('Missing endpoint for "%s" route.', routeType);
    }

    return endpoint;
  }

  /**
   * Creates the URL param string for the current menu option with column
   * header params and returns the URL parametized list.
   */
  getUrlParams() {
    const columnHeaderParams = {
      jsonType: "colHead",
      draw: 0,
    };
    const urlParams = Object.assign(
      {},
      columnHeaderParams,
      this.navigation.data
    );
    return $.param(urlParams);
  }

  _onLoad(columnModel) {
    const columns = tooltipConfigurator(columnModel.data);

    // create reporting options
    const options = {
      endpoint: this.endpoint,
      params: this.navigation.data,
      route: this.route,
      dt_config: this.dt_config,
      columns,
      customColumns: this.customColumns,
      customColumnsCreated: this.customColumnsCreated,
      linkClassName: this.linkClassName,
    };

    // save to history
    reportChannel.trigger(
      "history:add",
      new ReportHistoryModel({
        route: this.route,
        columns,
        customColumns: this.customColumns,
        customColumnsCreated: this.customColumnsCreated,
        url: {
          endpoint: this.endpoint,
          data: this.navigation.data,
        },
        updated: Date.now(),
      })
    );

    // announce config is ready and pass the options
    reportChannel.trigger(this.eventName, options);
  }

  _onLoadFailure(jqXHR) {
    // display error message
    globalChannel.trigger("status:show", {
      autoDismiss: false,
      type: "danger",
      message: errorParser(jqXHR),
    });
  }
}

export { DatatableConfigurator as default };
